
import { useEffect } from "react";
import { LocationPromptDialog } from "@/components/location/LocationPromptDialog";
import { useLocationServices } from "@/hooks/useLocationServices";
import { createLogger } from "@/utils/logger";

const logger = createLogger('ProgressiveLocationDialog');

interface ProgressiveLocationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEnableLocation: () => void;
  onMaybeLater: () => void;
  contextualTrigger?: string;
}

// Track when dialogs were shown to prevent duplicates
const shownDialogs = new Set<string>();

export const ProgressiveLocationDialog = ({
  open,
  onOpenChange,
  onEnableLocation,
  onMaybeLater,
  contextualTrigger
}: ProgressiveLocationDialogProps) => {
  const { setDoNotShowAgainPreference, locationState } = useLocationServices();
  
  // Prevent showing dialog if location is already being requested
  useEffect(() => {
    if (open && (locationState.isLoading || locationState.permissionTransitioning)) {
      logger.debug('Suppressing location dialog because location is already being requested');
      onOpenChange(false);
    }
  }, [open, locationState.isLoading, locationState.permissionTransitioning, onOpenChange]);
  
  // Prevent showing duplicate dialogs in the same session
  useEffect(() => {
    if (open && contextualTrigger) {
      const dialogKey = `dialog_${contextualTrigger}`;
      
      if (shownDialogs.has(dialogKey)) {
        logger.debug(`Preventing duplicate dialog for trigger: ${contextualTrigger}`);
        onOpenChange(false);
        return;
      }
      
      // Mark this dialog as shown
      shownDialogs.add(dialogKey);
    }
  }, [open, contextualTrigger, onOpenChange]);
  
  const handleDoNotShowAgain = (value: boolean) => {
    setDoNotShowAgainPreference(value);
    
    if (value) {
      // Clear all dialog tracking to respect user preference
      shownDialogs.clear();
    }
  };
  
  return (
    <LocationPromptDialog
      open={open}
      onOpenChange={onOpenChange}
      onEnableLocation={onEnableLocation}
      onMaybeLater={onMaybeLater}
      onDoNotShowAgain={handleDoNotShowAgain}
    />
  );
};
